import React, { useState } from 'react';
import styled from 'styled-components';
import { Bio } from '../../data/constants';
import { motion } from 'framer-motion';
import { FaEnvelope, FaLinkedin, FaGithub, FaCheck, FaCopy } from 'react-icons/fa';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  position: relative;
  z-index: 1;
  align-items: center;
  padding: 60px 40px 80px 40px;
  max-width: 1200px;
  margin: 0 auto;

  @media (max-width: 960px) {
    padding: 40px 20px 60px 20px;
  }
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;

  &::before {
    content: '📬';
    font-size: 22px;
  }
`;

const Title = styled.h2`
  font-size: 28px;
  font-weight: 600;
  color: ${({ theme }) => theme.text_primary || '#ffffff'};
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  margin: 0;
`;

const Subtitle = styled.p`
  font-size: 16px;
  color: ${({ theme }) => theme.text_secondary || '#b4b4b4'};
  text-align: center;
  max-width: 580px;
  line-height: 1.6;
  margin-bottom: 36px;
`;

const ContactCard = styled.div`
  width: 100%;
  max-width: 650px;
  background: rgba(255, 255, 255, 0.05);
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 24px;
  padding: 36px;
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.25);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 24px;

  @media (max-width: 640px) {
    padding: 24px 16px;
  }
`;

const EmailDisplay = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  background: rgba(255, 255, 255, 0.06);
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 16px;
  padding: 14px 20px;
  gap: 12px;

  @media (max-width: 640px) {
    flex-direction: column;
    align-items: stretch;
    text-align: center;
  }
`;

const EmailText = styled.span`
  color: #ffffff;
  font-size: 15px;
  font-weight: 500;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const CopyButton = styled.button`
  background: rgba(255, 255, 255, 0.1);
  color: #ffffff;
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 12px;
  padding: 8px 16px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  transition: all 0.25s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.2);
    border-color: rgba(255, 255, 255, 0.35);
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 16px;
  width: 100%;
  justify-content: center;
  flex-wrap: wrap;
`;

const ActionLink = styled.a`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 12px 24px;
  border-radius: 16px;
  text-decoration: none;
  font-size: 14px;
  font-weight: 600;
  color: #ffffff;
  background: rgba(255, 255, 255, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.15);
  backdrop-filter: blur(10px);
  transition: all 0.3s cubic-bezier(0.23, 1, 0.32, 1);
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.15);

  &:hover {
    background: rgba(255, 255, 255, 0.16);
    border-color: rgba(255, 255, 255, 0.3);
    transform: translateY(-2px);
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.25);
  }

  @media (max-width: 640px) {
    width: 100%;
  }
`;

const FooterText = styled.div`
  margin-top: 40px;
  font-size: 13px;
  color: ${({ theme }) => theme.text_secondary || '#888888'};
  text-align: center;
`;

const Contact = () => {
  const [copied, setCopied] = useState(false);
  const emailAddress = 'ronitsabhaya75@gmail.com';

  const handleCopy = () => {
    navigator.clipboard.writeText(emailAddress);
    setCopied(true);
    setTimeout(() => setCopied(false), 2500);
  };

  return (
    <Container id="contact">
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, amount: 0.3 }}
        transition={{ duration: 0.6 }}
        style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}
      >
        <SectionHeader>
          <Title>Get In Touch</Title>
        </SectionHeader>
        <Subtitle>
          I'm currently looking for new opportunities and collaborations. Whether you have a question, a project idea, or just want to say hi, feel free to reach out!
        </Subtitle>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        whileInView={{ opacity: 1, scale: 1 }}
        viewport={{ once: true, amount: 0.3 }}
        transition={{ duration: 0.6, delay: 0.2 }}
        style={{ width: '100%', display: 'flex', justifyContent: 'center' }}
      >
        <ContactCard>
          <EmailDisplay>
            <EmailText>{emailAddress}</EmailText>
            <CopyButton onClick={handleCopy}>
              {copied ? (
                <>
                  <FaCheck style={{ color: '#4ade80' }} /> Copied!
                </>
              ) : (
                <>
                  <FaCopy /> Copy
                </>
              )}
            </CopyButton>
          </EmailDisplay>

          <ButtonGroup>
            <ActionLink href={`mailto:${emailAddress}`}>
              <FaEnvelope /> Send Email
            </ActionLink>
            <ActionLink href={Bio.linkedin} target="_blank" rel="noopener noreferrer">
              <FaLinkedin /> LinkedIn
            </ActionLink>
            <ActionLink href={Bio.github} target="_blank" rel="noopener noreferrer">
              <FaGithub /> GitHub
            </ActionLink>
          </ButtonGroup>
        </ContactCard>
      </motion.div>

      <FooterText>
        © {new Date().getFullYear()} {Bio.name}. Designed & Built with React & Styled Components.
      </FooterText>
    </Container>
  );
};

export default Contact;
